import { message } from 'antd';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PaginationMeta from '../../types/PaginationMeta';
import UserAPI, { DEFAULT_USER_PAGE_SIZE } from '../UserAPI';
import User, { UserRole } from '../types/User';
import { useUser } from '../userContext';
import { sortInstructorsByFavorite } from '../userUtils';

type UseInstructorOptionsParams = {
  enabled?: boolean;
  hydrateSelectedIds?: (User['id'] | null | undefined)[];
  pageSize?: number;
};

type InstructorPageRequest = {
  page: number;
  query: string;
};

const getUniqueInstructors = (instructors: User[]) => {
  const byId = new Map<User['id'] | User['email'], User>();

  instructors.forEach((instructor) => {
    byId.set(instructor.id ?? instructor.email, instructor);
  });

  return Array.from(byId.values());
};

const getUniqueIds = (ids: (User['id'] | null | undefined)[] = []) =>
  Array.from(
    new Set(ids.filter((id): id is NonNullable<User['id']> => id != null)),
  );

export default function useInstructorOptions({
  enabled = true,
  hydrateSelectedIds = [],
  pageSize = DEFAULT_USER_PAGE_SIZE,
}: UseInstructorOptionsParams = {}) {
  const { t } = useTranslation();
  const [currentUser] = useUser();
  const [loadedInstructors, setLoadedInstructors] = useState<User[]>([]);
  const [hydratedInstructors, setHydratedInstructors] = useState<User[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(
    null,
  );
  const [currentQuery, setCurrentQuery] = useState('');
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [hydratingSelected, setHydratingSelected] = useState(false);
  const debounceTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const pageRequestId = useRef(0);
  const hydrateRequestId = useRef(0);
  const activeNextPageRequest = useRef<InstructorPageRequest | null>(null);

  const instructors = useMemo(
    () =>
      sortInstructorsByFavorite(
        getUniqueInstructors([...hydratedInstructors, ...loadedInstructors]),
        currentUser,
      ),
    [currentUser, hydratedInstructors, loadedInstructors],
  );

  const selectedIdKey = useMemo(
    () =>
      getUniqueIds(hydrateSelectedIds)
        .sort((left, right) => left - right)
        .join(','),
    [hydrateSelectedIds],
  );

  const reportError = useCallback(
    (error: unknown) => {
      if (isAxiosError(error)) {
        message.error(
          error.response?.data.message || t('errors.somethingWentWrong'),
        );
        return;
      }

      message.error(t('errors.somethingWentWrong'));
    },
    [t],
  );

  const loadInstructors = useCallback(
    async ({
      append = false,
      page = 1,
      query = currentQuery,
    }: {
      append?: boolean;
      page?: number;
      query?: string;
    } = {}) => {
      if (!enabled) {
        activeNextPageRequest.current = null;
        return;
      }

      const requestId = pageRequestId.current + 1;
      pageRequestId.current = requestId;
      const pagination = { page, pageSize };

      if (append) {
        setLoadingNextPage(true);
      } else {
        activeNextPageRequest.current = null;
        setLoadingNextPage(false);
        setLoadingPage(true);
      }

      try {
        const { data } = await UserAPI.getPaginatedUsers({
          roles: [UserRole.Instructor],
          ...(query ? { query } : {}),
          ...pagination,
        });

        if (pageRequestId.current !== requestId) {
          return;
        }

        setCurrentQuery(query);
        setPaginationMeta(data.paginationMeta);
        setLoadedInstructors((currentInstructors) =>
          append
            ? getUniqueInstructors([...currentInstructors, ...data.data])
            : data.data,
        );
      } catch (error) {
        if (pageRequestId.current === requestId) {
          reportError(error);
        }
      } finally {
        if (pageRequestId.current === requestId) {
          if (append) {
            setLoadingNextPage(false);
          } else {
            setLoadingPage(false);
          }
        }
      }
    },
    [currentQuery, enabled, pageSize, reportError],
  );

  const loadFirstPage = useCallback(
    (query = currentQuery) => {
      if (debounceTimeoutId.current) {
        clearTimeout(debounceTimeoutId.current);
        debounceTimeoutId.current = null;
      }
      return loadInstructors({ page: 1, query });
    },
    [currentQuery, loadInstructors],
  );

  const searchInstructors = useCallback(
    (query: string) => {
      if (debounceTimeoutId.current) {
        clearTimeout(debounceTimeoutId.current);
      }

      setCurrentQuery(query);
      setLoadedInstructors([]);
      setPaginationMeta(null);
      activeNextPageRequest.current = null;
      pageRequestId.current += 1;
      setLoadingNextPage(false);
      setLoadingPage(true);

      debounceTimeoutId.current = setTimeout(() => {
        loadFirstPage(query);
      }, 500);
    },
    [loadFirstPage],
  );

  const hasMoreInstructors =
    !!paginationMeta && paginationMeta.page < paginationMeta.lastPage;

  const loadMoreInstructors = useCallback(() => {
    const nextPage = (paginationMeta?.page ?? 0) + 1;
    const activeRequest = activeNextPageRequest.current;

    if (
      !hasMoreInstructors ||
      loadingNextPage ||
      loadingPage ||
      (activeRequest?.page === nextPage && activeRequest.query === currentQuery)
    ) {
      return;
    }

    const nextPageRequest = { page: nextPage, query: currentQuery };
    activeNextPageRequest.current = nextPageRequest;

    loadInstructors({
      append: true,
      page: nextPage,
      query: currentQuery,
    }).finally(() => {
      if (activeNextPageRequest.current === nextPageRequest) {
        activeNextPageRequest.current = null;
      }
    });
  }, [
    currentQuery,
    hasMoreInstructors,
    loadInstructors,
    loadingNextPage,
    loadingPage,
    paginationMeta?.page,
  ]);

  useEffect(() => {
    if (!enabled) {
      hydrateRequestId.current += 1;
      setHydratedInstructors([]);
      setHydratingSelected(false);
      return;
    }

    const ids = selectedIdKey ? selectedIdKey.split(',').map(Number) : [];
    if (ids.length === 0) {
      hydrateRequestId.current += 1;
      setHydratedInstructors([]);
      setHydratingSelected(false);
      return;
    }

    const requestId = hydrateRequestId.current + 1;
    hydrateRequestId.current = requestId;

    const hydrateInstructors = async () => {
      setHydratingSelected(true);
      const idSet = new Set(ids);
      let matchingInstructors: User[] = [];

      try {
        const pagination = {
          page: 1,
          pageSize: Math.max(ids.length, pageSize),
        };
        const { data } = await UserAPI.getPaginatedUsers({
          roles: [UserRole.Instructor],
          userIds: ids,
          ...pagination,
        });
        matchingInstructors = data.data.filter(
          (instructor) => instructor.id !== null && idSet.has(instructor.id),
        );
      } catch {
        matchingInstructors = [];
      }

      const foundIds = new Set(
        matchingInstructors.map((instructor) => instructor.id),
      );
      const missingIds = ids.filter((id) => !foundIds.has(id));
      const individuallyLoaded = await Promise.all(
        missingIds.map(async (id) => {
          try {
            const { data } = await UserAPI.getUser(id);
            return data.isInstructor ? data : undefined;
          } catch {
            return undefined;
          }
        }),
      );

      if (hydrateRequestId.current !== requestId) {
        return;
      }

      setHydratedInstructors(
        getUniqueInstructors([
          ...matchingInstructors,
          ...individuallyLoaded.filter(
            (instructor): instructor is User => !!instructor,
          ),
        ]),
      );
      setHydratingSelected(false);
    };

    hydrateInstructors().catch((error) => {
      if (hydrateRequestId.current === requestId) {
        setHydratingSelected(false);
        reportError(error);
      }
    });
  }, [enabled, pageSize, reportError, selectedIdKey]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutId.current) {
        clearTimeout(debounceTimeoutId.current);
      }
    };
  }, []);

  return {
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage: loadFirstPage,
    loadMoreInstructors,
    loadingInstructorOptions:
      loadingPage || loadingNextPage || hydratingSelected,
    loadingInstructors: loadingPage || hydratingSelected,
    searchInstructors,
  };
}
